import React, { ReactElement, useMemo } from 'react';

import classNames from 'classnames';

import Call from '@ast/magma/components/icon/icons/Call';
import MobileDevice from '@ast/magma/components/icon/icons/MobileDevice';
import Suitcase from '@ast/magma/components/icon/icons/Suitcase';

import { PhoneSmsEnrollmentStatus } from '@app/queryTyping';

import { FieldInfoGroupField } from '@app/common/configurable-wizards';
import { FieldComponent, FieldLocator } from '@app/common/configurable-wizards/types';
import { isFieldInfoGroupField } from '@app/common/configurable-wizards/utils';

import { useUserSettingsTranslation } from '@app/widgets/contacts-and-settings/hooks/useUserSettingsTranslation';
import { formatPhone } from '@app/widgets/contacts-and-settings/utils/labelFormatters';
import { getMappedFields } from '@app/widgets/contacts-and-settings/utils/subFieldsMapper';

import { PhoneItemFields } from '../../types';

import styles from './PhoneFieldsListItem.pcss';

export const groupIconMap: Record<string, ReactElement> = {
  [PhoneItemFields.MobilePhoneFieldGroup]: <MobileDevice />,
  [PhoneItemFields.HomePhoneFieldGroup]: <Call />,
  [PhoneItemFields.WorkPhoneFieldGroup]: <Suitcase />,
};

export const PhoneFieldsListItem: FieldComponent<FieldInfoGroupField> = ({ field }) => {
  const { t } = useUserSettingsTranslation();

  const fieldMeta = useMemo(
    () => getMappedFields(field.subFields),
    [field],
  );
  const phoneNumber = useMemo(() => formatPhone(fieldMeta), [fieldMeta]);
  const groupIcon = useMemo(() => {
    const groupId = PhoneItemFields[field.id as keyof typeof PhoneItemFields];

    return (
      <span
        className={styles.icon}
        data-stable-name={`${field.id}Icon`}
      >
        {groupIconMap[groupId] || groupIconMap[PhoneItemFields.HomePhoneFieldGroup]}
      </span>
    );
  }, [field]);

  const isPrimary = fieldMeta.IsPrimary?.value;
  const isEnrolled = fieldMeta.SmsEnrollmentStatus?.value.toUpperCase() === PhoneSmsEnrollmentStatus.ENROLLED;
  const isPending = fieldMeta.SmsEnrollmentStatus?.value.toUpperCase() === PhoneSmsEnrollmentStatus.OTPPENDING;

  return (
    <div
      data-stable-name={`${isPrimary ? 'Primary' : 'Secondary'}PhoneItem`}
      className={styles.listItem}
    >
      {groupIcon}
      <div className={styles.details}>
        <div data-stable-name="PhoneNumber">{phoneNumber}</div>
        <div
          className={classNames(
            styles.primary,
            !isPrimary && styles.hidden,
          )}
        >
          {t(
            'phone-fields-list-item.primary|Primary phone number label',
            'Primary phone',
          )}
        </div>
        <div
          className={classNames(
            styles.textMessaging,
            isPending && styles.pending,
          )}
        >
          { isEnrolled && t(
            'phone-fields-list-item.text-messaging|Text messaging status',
            'Used for text messaging',
          )}

          { isPending && t(
            'phone-fields-list-item.text-messaging-pending|Text messaging for pending status',
            'Verification code pending',
          )}
        </div>
      </div>
    </div>
  );
};

export const PhoneFieldsListItemLocator: FieldLocator = ({ field }) => (
  isFieldInfoGroupField(field) && field.id in PhoneItemFields
    ? PhoneFieldsListItem
    : undefined
);
